import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectPreferredLanguage, setApplicationRoutingData, setPreferredLanguage } from "features/authSlice";
import { useSendUserInteractedMutation, type MyLoanInfo } from "pages/application/applicationService";
import { useGetFeatureFlagsQuery } from "services/common/featureFlagService";
import { persistor } from "store";
import { resolveLanguageForLoanEntry, shouldPersistLoanEntryUserLanguage } from "utilities/getPreferredLanguage";
import { enterPostAuthWithLangPref } from "utilities/langPrefCookie";

export const useNavigateToPostAuth = (): ((loan: MyLoanInfo) => void) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const reduxLanguage = useSelector(selectPreferredLanguage);
  const { data: featureFlag } = useGetFeatureFlagsQuery();
  const [sendUserInteracted] = useSendUserInteractedMutation();
  const isPreferredLanguageEnabled = Boolean(featureFlag?.preferredLanguageEnabled);

  return useCallback(
    (loan: MyLoanInfo) => {
      const {
        nhApplicationId,
        nhApplicationStatus,
        nhDealApplicationId,
        applicationRoleType,
        dealApplicationId,
        loanId,
        hasPal,
        prospectId,
        loanPurpose,
        leadProvider,
        isConsentCollected,
        userRole,
        inquiryId,
        loanOfficerId,
        coBorrowerHasOwnNhApplication,
        nhApplicationPath,
        loanNumber,
        applicationNumber,
        userType,
        encompassLoanId,
        dealId,
        dealBorrowerId,
      } = loan;

      void sendUserInteracted({
        applicationId: dealApplicationId ?? "",
        prospectId: prospectId ?? "",
        userType,
      });

      dispatch(
        setApplicationRoutingData({
          nhApplicationId,
          nhApplicationStatus,
          nhDealApplicationId,
          applicationRoleType,
          dealApplicationId,
          loanId,
          hasPal,
          prospectId,
          loanPurpose,
          leadProvider,
          isConsentCollected,
          userRole,
          inquiryId,
          loanOfficerId,
          coBorrowerHasOwnNhApplication,
          nhApplicationPath,
          loanNumber,
          applicationNumber,
          encompassLoanId,
          dealId,
          dealBorrowerId,
        }),
      );

      const language = resolveLanguageForLoanEntry(loan, reduxLanguage);
      if (isPreferredLanguageEnabled && shouldPersistLoanEntryUserLanguage(language, reduxLanguage)) {
        dispatch(setPreferredLanguage(language));
      }

      void enterPostAuthWithLangPref({
        language,
        isPreferredLanguageEnabled,
        spaNavigate: () => navigate("/post-auth", { replace: true }),
        persistFlush: () => persistor.flush(),
      });
    },
    [dispatch, isPreferredLanguageEnabled, navigate, reduxLanguage, sendUserInteracted],
  );
};
